'use client';

import * as React from 'react';
import { Button as BaseButton } from '@base-ui/react/button';
import { clsx } from 'clsx';
import styles from './Button.module.css';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Visual variant
   * - primary: Filled with primary blue (contained)
   * - secondary: Outlined with primary blue border
   * - tertiary: Text only, minimal
   * - accent: Pink filled for high-visibility CTAs
   * - destructive: Red filled for irreversible actions (always confirm first)
   */
  variant?: 'primary' | 'secondary' | 'tertiary' | 'accent' | 'destructive';

  /**
   * Text color for the `tertiary` variant only — ignored by other variants.
   * - blue (default): standard low-emphasis actions
   * - pink: inline edit-style affordances (e.g. "Edit details")
   * - red: low-emphasis destructive actions (e.g. "Delete education")
   */
  color?: 'blue' | 'pink' | 'red';

  /**
   * Size variant
   * - md: Default 44px height
   * - lg: Hero/landing 48px height
   * - sm: Compact 36px height
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Full width button
   */
  fullWidth?: boolean;

  /**
   * Remove horizontal padding — useful for tertiary inline/text links
   */
  noPadding?: boolean;

  /**
   * Loading state
   */
  loading?: boolean;

  /**
   * Rotate the icon 180deg on hover — an opt-in affordance for buttons that
   * reveal or expand something. Off by default.
   */
  iconSpin?: boolean;

  /**
   * Icon to display before children
   */
  startIcon?: React.ReactNode;

  /**
   * Icon to display after children
   */
  endIcon?: React.ReactNode;

  /**
   * Additional class names
   */
  className?: string;

  /**
   * Children content
   */
  children?: React.ReactNode;

  /**
   * Render as another element, e.g. `render={<a href="…" />}` for a link
   * styled as a button. Pair a non-button element with `nativeButton={false}`.
   */
  render?: BaseButton.Props['render'];

  /**
   * Whether `render` produces a native `<button>`. Defaults to true.
   */
  nativeButton?: boolean;
}

/**
 * Button Component
 *
 * Laws:
 * - No gradient buttons
 * - Accent pink fill is exclusive to the accent variant; tertiary may use pink *text* via `color="pink"`
 * - Destructive red is exclusive to irreversible actions (delete, remove)
 * - Hover = color shift for primary/secondary/destructive, lift for accent, no-underline for tertiary
 * - Icon rotate on hover is opt-in via `iconSpin`, never automatic
 * - Height: sm=36, md=44, lg=48
 * - Radius: always 4px (radius-sm)
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'primary',
      color = 'blue',
      size = 'md',
      fullWidth = false,
      noPadding = false,
      loading = false,
      iconSpin = false,
      startIcon,
      endIcon,
      className,
      disabled,
      children,
      ...props
    },
    ref
  ) => {
    const isDisabled = disabled || loading;

    return (
      <BaseButton
        ref={ref}
        disabled={isDisabled}
        className={clsx(
          styles.button,
          styles[variant],
          variant === 'tertiary' && color === 'pink' && styles.tertiaryPink,
          variant === 'tertiary' && color === 'red' && styles.tertiaryRed,
          styles[size],
          fullWidth && styles.fullWidth,
          noPadding && styles.noPadding,
          loading && styles.loading,
          iconSpin && styles.iconSpin,
          !loading && startIcon && styles.hasStartIcon,
          !loading && endIcon && styles.hasEndIcon,
          className
        )}
        {...props}
      >
        {loading && (
          <span className={styles.spinner} aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" className={styles.spinnerIcon}>
              <circle
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray="31.416"
                strokeDashoffset="10"
              />
            </svg>
          </span>
        )}
        {!loading && startIcon && <span className={styles.icon}>{startIcon}</span>}
        <span className={styles.label}>{children}</span>
        {!loading && endIcon && <span className={styles.icon}>{endIcon}</span>}
      </BaseButton>
    );
  }
);

Button.displayName = 'Button';

export default Button;
